import { useMemo } from "react";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { InflightWorker, PoolQueueKey } from "@/services/workerApi";
import { WorkerInfo } from "@/services/scrapingStatsApi";

// Files du pool (globales) avec libellé court + couleur.
const POOL: { key: PoolQueueKey; label: string; color: string }[] = [
  { key: "enrichment", label: "Candidats", color: "hsl(var(--chart-1))" },
  { key: "company_enrichment", label: "Entreprises", color: "hsl(var(--chart-2))" },
  { key: "school_enrichment", label: "Écoles", color: "hsl(var(--chart-3))" },
  { key: "company_jobs", label: "Offres", color: "hsl(var(--chart-4))" },
  { key: "search", label: "Recherche", color: "hsl(var(--chart-5))" },
];

const chartConfig: ChartConfig = Object.fromEntries(
  POOL.map((p) => [p.key, { label: p.label, color: p.color }]),
);

function workerLabel(workerId: string, info?: WorkerInfo): string {
  const short = workerId.slice(0, 4);
  if (info?.is_anonymous) return `Anonyme · ${short}`;
  if (info?.user_name) return `${info.user_name} · ${short}`;
  return workerId.slice(0, 10);
}

// Tâches actuellement détenues (processing_*) par worker, empilées par type de
// file. La vue « qui travaille maintenant » + concentration de la charge live.
export function InflightByWorkerBarChart({
  workers,
  infoFor,
  loading,
  topN = 15,
}: {
  workers: InflightWorker[];
  infoFor?: (workerId: string) => WorkerInfo | undefined;
  loading?: boolean;
  topN?: number;
}) {
  const data = useMemo(() => {
    const sorted = [...workers].sort((a, b) => b.total - a.total).slice(0, topN);
    return sorted.map((w) => {
      const point: Record<string, number | string> = {
        label: workerLabel(w.worker_id, infoFor?.(w.worker_id)),
      };
      for (const p of POOL) point[p.key] = w.counts?.[p.key] ?? 0;
      return point;
    });
  }, [workers, infoFor, topN]);

  if (loading) {
    return <div className="text-sm text-muted-foreground py-8 text-center">Chargement...</div>;
  }
  if (data.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-8 text-center">
        Aucune tâche en cours sur la flotte
      </div>
    );
  }

  return (
    <ChartContainer
      config={chartConfig}
      className="w-full"
      style={{ height: Math.max(220, data.length * 32 + 40) }}
    >
      <BarChart data={data} layout="vertical" margin={{ left: 12, right: 16 }}>
        <CartesianGrid horizontal={false} />
        <XAxis type="number" tickLine={false} axisLine={false} allowDecimals={false} />
        <YAxis type="category" dataKey="label" tickLine={false} axisLine={false} width={170} />
        <ChartTooltip content={<ChartTooltipContent />} />
        {POOL.map((p, idx) => (
          <Bar
            key={p.key}
            dataKey={p.key}
            stackId="a"
            fill={`var(--color-${p.key})`}
            radius={
              idx === 0
                ? [4, 0, 0, 4]
                : idx === POOL.length - 1
                ? [0, 4, 4, 0]
                : [0, 0, 0, 0]
            }
          />
        ))}
      </BarChart>
    </ChartContainer>
  );
}
